import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { AuthBanner, AuthCard, AuthLink, AuthSubmit } from "@/components/auth/AuthCard.jsx";
import { Field, IconInput, PasswordInput } from "@/components/ui/Form.jsx";
import { useAuth, landingRouteFor } from "@/context/auth.js";
import { apiErrorMessage } from "@/lib/apiClient.js";

/*
 * Replaces frontend/app/(auth)/login/page.js — markup from html-markup/login.html,
 * behaviour from the page it replaces.
 *
 * The ?error= / ?message= contract is kept exactly: the desktop app and existing
 * links point at /login?error=session-expired, and RequireAuth produces the same
 * query string when it bounces someone.
 */

const schema = z.object({
    email: z
        .string()
        .trim()
        .min(1, "Please enter your email address")
        .email("That does not look like a valid email address"),
    password: z.string().min(1, "Please enter your password"),
});

/** ?error= / ?message= → the banner the current page shows for each. */
function bannerFor(params) {
    const error = params.get("error");
    const message = params.get("message");

    if (error === "session-expired") {
        return { tone: "error", text: "Your session has expired. Please sign in again." };
    }
    if (error) return { tone: "error", text: "Authentication failed. Please try again." };
    if (message === "password-changed") {
        return {
            tone: "success",
            text: "Password changed successfully! Please sign in with your new password.",
        };
    }
    return null;
}

export default function Login() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { login, isAuthenticated, user } = useAuth();

    // Seeded from the URL, then owned by the form: a failed submit replaces the
    // "session expired" notice rather than stacking a second banner under it.
    const [banner, setBanner] = useState(() => bannerFor(params));

    const {
        register,
        handleSubmit,
        setFocus,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        // Validate on submit, then live once a field has been reported — the
        // markup's contract, and it avoids scolding someone mid-way through
        // typing their email.
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: { email: "", password: "" },
    });

    useEffect(() => setFocus("email"), [setFocus]);

    /*
     * ONE redirect authority, for the same reason RequireAuth is: this effect
     * owns leaving the login page, whether the session was already there or was
     * just created by the submit below.
     *
     * Doing it in both places raced — the effect fires the moment login() sets
     * the session, and its plain landing route beat the submit handler's
     * navigate(next), so ?next= was silently dropped.
     *
     * It also covers a case the Next.js app missed entirely: hitting /login with
     * a live session showed an empty form, and signing in again was the only way
     * out.
     *
     * `next` only ever comes from RequireAuth, which builds it from a route in
     * this app, so it cannot be an off-site redirect.
     */
    useEffect(() => {
        if (!isAuthenticated) return;
        navigate(params.get("next") || landingRouteFor(user?.role), { replace: true });
    }, [isAuthenticated, user, navigate, params]);

    const onSubmit = async (values) => {
        setBanner(null);
        try {
            await login(values);
            // No navigate here — the effect above sees isAuthenticated flip.
        } catch (error) {
            setBanner({
                tone: "error",
                text:
                    error?.response?.status === 401
                        ? "Invalid email or password"
                        : apiErrorMessage(error, "Login failed. Please try again."),
            });
        }
    };

    return (
        <AuthCard
            eyebrow="Sign in"
            title="Welcome back"
            subtitle="Enter your credentials to access your dashboard."
            footer={
                <>
                    <p className="text-[13px] text-slate-400 dark:text-slate-500">
                        Trouble signing in? Contact your organization admin.
                    </p>
                    <p className="mt-2.5 text-[13px] text-slate-500 dark:text-slate-400">
                        Setting up a new organization?{" "}
                        <AuthLink to="/register">Register here</AuthLink>
                    </p>
                </>
            }
        >
            <AuthBanner tone={banner?.tone}>{banner?.text}</AuthBanner>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Field label="Email address" htmlFor="email" error={errors.email?.message}>
                    <IconInput
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@company.com"
                        icon={
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                aria-hidden="true"
                            >
                                <rect x="2.5" y="5" width="19" height="14" rx="2" />
                                <path d="m3 7 9 6 9-6" />
                            </svg>
                        }
                        {...register("email")}
                    />
                </Field>

                <Field
                    label="Password"
                    htmlFor="password"
                    error={errors.password?.message}
                    className="mt-4"
                >
                    <PasswordInput
                        id="password"
                        autoComplete="current-password"
                        placeholder="••••••••"
                        icon={
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                aria-hidden="true"
                            >
                                <rect x="4" y="10.5" width="16" height="10.5" rx="2" />
                                <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
                            </svg>
                        }
                        {...register("password")}
                    />
                </Field>

                <div className="mt-7">
                    <AuthSubmit loading={isSubmitting} loadingLabel="Signing in…">
                        Sign in to dashboard
                    </AuthSubmit>
                </div>
            </form>
        </AuthCard>
    );
}
